export const NEWSLETTER_STATUS = {
  draft: 1,
  published: 2,
};

const REDIRECT_KEY = '@newsletter/redirect';

export const getNewsletterLink = (id, domain) =>
  `${domain ? `https://${domain}` : window.location.origin}/newsletter/${id}`;

export const rememberNewsletterRedirect = (pathname) => {
  try {
    if (/^\/newsletter\/[\w-]+$/.test(pathname)) {
      sessionStorage.setItem(REDIRECT_KEY, pathname);
    }
  } catch (err) {}
};

export const consumeNewsletterRedirect = () => {
  try {
    const pathname = sessionStorage.getItem(REDIRECT_KEY);
    sessionStorage.removeItem(REDIRECT_KEY);
    return pathname;
  } catch (err) {
    return null;
  }
};

const ALLOWED_TAGS = [
  'P', 'BR', 'SPAN', 'STRONG', 'B', 'EM', 'I', 'U', 'S', 'A',
  'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
  'UL', 'OL', 'LI', 'BLOCKQUOTE', 'PRE',
  'IMG', 'IFRAME', 'VIDEO',
];

const EMBED_HOSTS = [
  'www.youtube.com',
  'youtube.com',
  'www.youtube-nocookie.com',
  'player.vimeo.com',
  'open.spotify.com',
];

const SPOTIFY_URL = /^(?:https?:\/\/)?open\.spotify\.com\/(?:intl-[a-z-]+\/)?(?:embed\/)?(track|album|playlist|episode|show|artist)\/([A-Za-z0-9]+)/i;
const SPOTIFY_URI = /^spotify:(track|album|playlist|episode|show|artist):([A-Za-z0-9]+)$/i;

// Link de compartilhamento do Spotify (ou URI spotify:...) -> URL do player embedável.
export const toSpotifyEmbedUrl = (value) => {
  const match =
    (value || '').trim().match(SPOTIFY_URL) ||
    (value || '').trim().match(SPOTIFY_URI);

  return match
    ? `https://open.spotify.com/embed/${match[1].toLowerCase()}/${match[2]}`
    : null;
};

const parseUrl = (value) => {
  try {
    return new URL(value, window.location.origin);
  } catch (err) {
    return null;
  }
};

const isSafeUrl = (value, protocols) => {
  const url = parseUrl(value);
  return !!url && protocols.includes(url.protocol);
};

const isEmbedUrl = (value) => {
  const url = parseUrl(value);
  return (
    !!url && url.protocol === 'https:' && EMBED_HOSTS.includes(url.hostname)
  );
};

const sanitizeNode = (node) => {
  Array.from(node.children).forEach((child) => {
    if (!ALLOWED_TAGS.includes(child.tagName)) {
      child.remove();
      return;
    }

    const src = child.getAttribute('src');
    const href = child.getAttribute('href');
    const width = child.getAttribute('width');
    const className = (child.getAttribute('class') || '')
      .split(/\s+/)
      .filter((name) => name.startsWith('ql-'))
      .join(' ');

    Array.from(child.attributes).forEach((attr) =>
      child.removeAttribute(attr.name)
    );

    if (className) child.setAttribute('class', className);

    switch (child.tagName) {
      case 'A':
        if (isSafeUrl(href, ['http:', 'https:', 'mailto:'])) {
          child.setAttribute('href', href);
          child.setAttribute('target', '_blank');
          child.setAttribute('rel', 'noopener noreferrer');
        }
        break;
      case 'IMG':
        if (!isSafeUrl(src, ['http:', 'https:'])) {
          child.remove();
          return;
        }
        child.setAttribute('src', src);
        if (/^\d{1,3}%$/.test(width || '')) child.setAttribute('width', width);
        break;
      case 'VIDEO':
        if (!isSafeUrl(src, ['http:', 'https:'])) {
          child.remove();
          return;
        }
        child.setAttribute('src', src);
        child.setAttribute('controls', '');
        child.setAttribute('controlsList', 'nodownload');
        child.setAttribute('preload', 'metadata');
        break;
      case 'IFRAME': {
        // Spotify só embeda pelo caminho /embed/: normaliza também postagens salvas com o link comum.
        const spotifyUrl = toSpotifyEmbedUrl(src);
        const embedSrc = spotifyUrl || src;

        if (!isEmbedUrl(embedSrc)) {
          child.remove();
          return;
        }
        if (parseUrl(embedSrc).hostname === 'open.spotify.com' && !spotifyUrl) {
          child.remove();
          return;
        }
        child.setAttribute('src', embedSrc);
        child.setAttribute('frameborder', '0');
        child.setAttribute('allowfullscreen', 'true');
        if (spotifyUrl) {
          child.setAttribute(
            'allow',
            'autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture'
          );
          child.setAttribute('loading', 'lazy');
        }
        break;
      }
      default:
    }

    sanitizeNode(child);
  });
};

export const sanitizeNewsletterHtml = (html) => {
  if (!html) return '';

  const doc = new DOMParser().parseFromString(html, 'text/html');
  sanitizeNode(doc.body);

  return doc.body.innerHTML;
};

export const isNewsletterContentEmpty = (html) => {
  if (!html) return true;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (
    !doc.body.textContent.trim() &&
    !doc.body.querySelector('img, iframe, video')
  );
};
